import { Button } from '@xo-union/tk-component-buttons';
import { FullBleedContainer, TopLevelContainer, Column, Row } from '@xo-union/tk-component-grid';
import Icon from '@xo-union/tk-component-icons';
import React, { RefObject, useContext, useMemo, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { getTargetOrigin } from '../../helpers/getTargetOrigin';
import { useRsvpIsVisible } from '../../helpers/useRsvpIsVisible';
import { Snackbar } from '@xo-union/tk-component-alerts';
import { SnackbarInterface, DISMISS_TIMEOUT } from '../../helpers/snackbar';
import { TrackingContext } from '../../TrackingContext';
import { PagesFragment, usePagesQuery, useRegistrySummaryQuery } from '../../generated/graphql';
import UrlCopy from '../InfoHeader/UrlCopy';
import FullScreenModalHeader from '../Modals/FullScreenModal/Header';
import { useWorkflowUnstartedState } from '../RsvpPageCard/utils';
import { IconToggle, IconToggleContainer } from './IconToggle';
import useIsLiteSite, { PAGE_TYPE_LITE_SITE } from '../../../src/helpers/useIsLiteSite';
import { rsvpWorkflowRoute, addToRegistryRoute, previewRoute } from '../../../src/helpers/routes';
import styles from './previewHeader.scss';
import '@xo-union/tk-ui-typography/lib/globals.css';
import registryInfo from '../../helpers/registryInfo';
import { WEDDING_PAGE_TYPE } from '../../helpers/pages';
import { PreviewState } from './LiteSitePreview';
import { useRegistryPage } from '../../helpers/useRegistryPage';
import { ButtonDropdown, ButtonDropdownItem } from '../ButtonDropdown';
import { compose } from '@xo-union/react-css-modules';
import { useWebsiteLayoutContext } from '../../context/WebsiteLayoutContext';

interface Props {
  url: string;
  pages: PagesFragment[];
  currentPage?: PagesFragment | string;
  iframeRef: RefObject<HTMLIFrameElement>;
  setCurrentPage: (page: PagesFragment | string) => void;
  allowPaperPreview: boolean;
  selectedPreview: PreviewState;
  changeSelectedPreview: (selection: string) => void;
  rightRailToggle: () => void;
  editPageToggle: () => void;
  addPageToggle: (page?: PagesFragment) => void;
  showRsvpEmptyState: boolean;
  setShowRsvpEmptyState: (state: boolean) => void;
  className?: string;
}

interface AddNewPagesDropdownItemsProps {
  newPages: PagesFragment[];
  addPageToggle: (page?: PagesFragment) => void;
  addRsvp?: boolean;
  addRegistry?: boolean;
}

const skipAddPage = ['SchedulePage', 'WeddingPage', 'RegistryPage', 'LiteSitePage'];
const useNavLinks = (isLiteSite: boolean): PagesFragment[] => {
  const { data } = usePagesQuery();

  let pages = (data?.pages || []).filter(p => {
    if (!p.title) {
      return false;
    }

    if (isLiteSite && p.items.length < 1 && p.type !== PAGE_TYPE_LITE_SITE) {
      // If this page has no items, do not return it
      return false;
    }

    if (isLiteSite && p.type === WEDDING_PAGE_TYPE) {
      // Don't return a WeddingPage on LiteSites.
      // Instead the LiteSitePage will be used for the Home page.
      return false;
    }

    return true;
  });

  // Rename Our Wedding to Home for legacy reasons
  pages = useMemo(
    () =>
      pages?.map(p => {
        if (p.type === WEDDING_PAGE_TYPE) {
          return {
            ...p,
            title: 'Home',
          };
        }

        return p;
      }),
    [pages]
  );

  return pages;
};

// return remaining pages user can add to liteSite
const useLiteSiteNewPages = (isLiteSite: boolean): PagesFragment[] => {
  const { data } = usePagesQuery();

  let pages: PagesFragment[] = [];

  if (isLiteSite) {
    pages = (data?.pages || []).filter(p =>
      p.items.length < 1 && !skipAddPage.includes(p.type) && p.title ? true : false
    );
  }

  return pages;
};

const AddNewPagesDropdownItems = ({
  newPages,
  addPageToggle,
  addRsvp = false,
  addRegistry = false,
}: AddNewPagesDropdownItemsProps) => {
  const classes = compose({
    buttonDropdownItem: styles.buttonItem,
    textContainer: styles.buttonTextContainer,
    titleText: styles.buttonTitleText,
    itemLink: styles.buttonLink,
  });

  return (
    <>
      {addRsvp && <ButtonDropdownItem key="add-rsvp" to={rsvpWorkflowRoute} title="RSVP" classes={classes} />}
      {addRegistry && (
        <li key="add-regsitry" className={styles.buttonItem}>
          <a href={addToRegistryRoute} className={styles.linkItem}>
            Registry
          </a>
        </li>
      )}
      {newPages.map(page => (
        <ButtonDropdownItem
          key={page.id}
          onClick={() => addPageToggle(page)}
          title={page.title || ''}
          classes={classes}
        />
      ))}
    </>
  );
};

export const PreviewHeader: React.FC<Props> = ({
  url,
  currentPage,
  iframeRef,
  setCurrentPage,
  allowPaperPreview,
  selectedPreview,
  changeSelectedPreview,
  rightRailToggle,
  addPageToggle,
  editPageToggle,
  showRsvpEmptyState,
  setShowRsvpEmptyState,
  className = '',
}) => {
  const liteSite = useIsLiteSite();
  const { currentLayout } = useWebsiteLayoutContext();

  const navPages = useNavLinks(liteSite);
  const registryPage = useRegistryPage();
  const liteSiteNewPages = useLiteSiteNewPages(liteSite);
  const history = useHistory();
  const { track } = useContext(TrackingContext);
  const isRsvpVisible = useRsvpIsVisible();
  const { loading: rsvpLoading, isUnstartedState } = useWorkflowUnstartedState();
  const { data: registryData } = useRegistrySummaryQuery();
  const { hasAnyRegistry } = registryInfo(registryData?.registrySummary);
  const snackbar = useRef<SnackbarInterface>();

  const backHandler = () => {
    history.push('/gs/dashboard');
  };

  const onCopy = () => {
    track('Website Link Copied', {
      source: 'preview',
    });
    if (snackbar?.current) {
      snackbar.current.showAndDismiss({
        data: 'Website link copied',
        dismissTimeout: DISMISS_TIMEOUT,
      });
    }
  };

  const urlText = <span className={styles.urlText}>{url}</span>;

  const renderSubNav = () => {
    return (
      <ul className={styles.scrollNav}>
        {navPages.map(page => {
          return (
            <li
              key={page.id}
              className={`${page.show ? '' : styles.hidden}  ${
                typeof currentPage !== 'string' && !showRsvpEmptyState && page?.id === currentPage?.id
                  ? styles.active
                  : ''
              }`}
            >
              <Link
                key={`link-${page.id}`}
                className={`${styles.subNavItem}`}
                to={{ pathname: previewRoute, search: `?location=${page.routeName}` }}
                onClick={() => {
                  if (showRsvpEmptyState) {
                    setShowRsvpEmptyState(false);
                  }
                  subNavClick(page);
                }}
              >
                {page.type === 'WeddingPage' ? 'Home' : page.title}
                {!page.show && <Icon name="eye_closed" size="md" className={styles.closedEyeIcon} />}
              </Link>
            </li>
          );
        })}
        {!rsvpLoading && (
          <>
            {!isUnstartedState ? (
              <li className={` ${isRsvpVisible ? '' : styles.hidden}  ${'rsvp' === currentPage ? styles.active : ''}`}>
                <Link
                  className={`${styles.subNavItem}`}
                  to={{ pathname: previewRoute, search: `?location=rsvp` }}
                  onClick={() => subNavClick('rsvp')}
                >
                  RSVP
                  {!isRsvpVisible && <Icon name="eye_closed" size="md" className={styles.closedEyeIcon} />}
                </Link>
              </li>
            ) : (
              <li className={`${showRsvpEmptyState ? styles.active : ''}`}>
                <Link
                  className={`${styles.subNavItem}`}
                  to={{ pathname: previewRoute, search: `?location=` }}
                  onClick={() => {
                    setShowRsvpEmptyState(true);
                    subNavClick(navPages.find(p => p.routeName === '') || '');
                  }}
                >
                  RSVP <Icon name="eye_closed" size="md" className={styles.closedEyeIcon} />
                </Link>
              </li>
            )}
          </>
        )}
        {liteSite && hasAnyRegistry && registryPage && (
          <li
            className={`${registryPage?.show ? '' : styles.hidden} ${
              typeof currentPage !== 'string' && 'registry' === currentPage?.routeName ? styles.active : ''
            }`}
          >
            <Link
              className={`${styles.subNavItem}`}
              to={{ pathname: previewRoute, search: `?location=registry` }}
              onClick={() => subNavClick(registryPage)}
            >
              Registry
              {!registryPage?.show && <Icon name="eye_closed" size="md" className={styles.closedEyeIcon} />}
            </Link>
          </li>
        )}
        {liteSite && !!liteSiteNewPages.length && (
          <li>
            <ButtonDropdown
              size="md"
              color="tertiary"
              text="Add Pages"
              icon="caret_down"
              className={styles.buttonDropdown}
              listClassName={styles.buttonList}
            >
              <AddNewPagesDropdownItems
                newPages={liteSiteNewPages}
                addPageToggle={addPageToggle}
                addRsvp={isUnstartedState}
                addRegistry={!hasAnyRegistry}
              />
            </ButtonDropdown>
          </li>
        )}
      </ul>
    );
  };

  const subNavClick = (page: PagesFragment | string) => {
    setCurrentPage(page);
    if (iframeRef && iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({
          type: 'currentPage',
          data:
            typeof page === 'string'
              ? {
                  // GV will 404 if RSVP is hidden unless preview is passed
                  routeName: 'rsvp?preview=true',
                }
              : page,
        }),
        getTargetOrigin()
      );
    }
  };

  const allowEdit = currentPage !== 'rsvp' || currentPage === 'rsvp';

  // Show the first two icon toggles with a border if we allow paper preview
  const iconToggleBordered = allowPaperPreview ? styles.iconToggleItemBordered : '';

  const isSinglePageSite = currentLayout === 'single-page';
  const pageOrSection = isSinglePageSite ? 'Section' : 'Page';

  return (
    <>
      <Snackbar ref={snackbar} />
      <FullScreenModalHeader className={`${styles.previewHeader} ${className}`}>
        <Row className={styles.mobileTopLevelNav}>
          <Column xs="6">
            <div className={styles.mobileBackArrow} onClick={backHandler}>
              <Icon name="back" size="md" />
              <span className={styles.backCTA}>Exit Preview</span>
            </div>
          </Column>
          <Column xs="6" className={`${styles.mobileUrl}`}>
            <UrlCopy className={styles.mobileCopyUrl} url={url} onCopy={onCopy} copyCta="Copy URL" />
          </Column>
        </Row>

        <TopLevelContainer>
          <div className={styles.mobilePreviewHeaders}>
            <Row>
              <Column xs="12">
                <h2>
                  <span className={`${styles.mobilePageTitle}`}>Website Preview</span>
                </h2>
              </Column>
            </Row>
          </div>
          <Row>
            <Column className={styles.topLevelNav} xs="12" md="12" lg="12">
              <div className={styles.backArrow} onClick={backHandler}>
                <Icon name="back" size="md" />
                <span className={styles.backCTA}>Exit Preview</span>
              </div>

              <div className={`${styles.siteLink} ${styles.addressBar}`}>
                <Icon name="website" size="md" />
                {urlText}
                <UrlCopy className={styles.copyUrl} url={url} onCopy={onCopy} />
              </div>

              <div className={styles.iconToggle}>
                <IconToggleContainer size="md" label="View As" changeSelectedPreview={changeSelectedPreview}>
                  <IconToggle icon="desktop" className={iconToggleBordered} selected={selectedPreview === 'desktop'} />
                  <IconToggle icon="mobile" className={iconToggleBordered} selected={selectedPreview === 'mobile'} />
                  {allowPaperPreview && <IconToggle icon="category-inv" selected={selectedPreview === 'paper'} />}
                </IconToggleContainer>
              </div>

              <div className={styles.buttonOuter}>
                <div className={styles.buttonContainer}>
                  {allowEdit && (
                    <Button
                      size="md"
                      color="secondary"
                      block="md"
                      onClick={editPageToggle}
                      className={styles.editPageButton}
                      data-testid="editPageButton"
                    >
                      {`Edit ${pageOrSection}`}
                    </Button>
                  )}
                </div>
                <div className={styles.buttonContainer}>
                  <Button
                    size="md"
                    color="primary"
                    block="md"
                    onClick={rightRailToggle}
                    data-testid="changeThemeButton"
                    className={styles.changeThemeButton}
                  >
                    Change Your Theme
                  </Button>
                </div>
              </div>
            </Column>
          </Row>
        </TopLevelContainer>
        {selectedPreview !== 'paper' ? (
          <FullBleedContainer>
            <Row>
              <Column className={styles.subNavContainer} xs="12" md="12" lg="12">
                {renderSubNav()}
              </Column>
            </Row>
          </FullBleedContainer>
        ) : (
          <div className={styles.paperSubNavSpacer} />
        )}
      </FullScreenModalHeader>
    </>
  );
};

export default PreviewHeader;
